import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { NavigationRoutes } from 'common/constants';
import { ApiError, ApiResult } from 'common/repositories/common.model';
import { useFormState } from 'components/elements/form';
import Menu, { MenuDataType } from 'mantine-components/widget/menu';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import UniskaiButton, {
  UniskaiButtonProps,
} from 'templates/actions/uniskai-button';
import {
  UniskaiButtonType,
  generateUniskaiActionAsset,
} from 'templates/actions/utils';

export interface UniskaiFormHeaderActionProps<T = any> {
  data?: T;

  // to acquire refresh data
  query?: UseQueryResult<ApiResult<T>, ApiError>;
  // to acquire action fn
  saveMutation?: UseMutationResult;

  additionalRefresh?: () => void;
  actions?: (MenuDataType & { type?: UniskaiButtonType })[];

  // for custom
  customs?: UniskaiButtonProps[];
}

const uniskaiActionsOrder: [
  UniskaiButtonType,
  boolean,
  'view' | 'default' | 'default-sales',
][] = [
  ['refresh', false, 'view'], // form disable
  ['revert-draft', false, 'view'], // form disable
  ['request-approval', false, 'view'], // form disable
  ['revert-void', false, 'view'], // form disable
  ['delete', false, 'view'], // form disable
  ['void', false, 'view'], // form disable
  ['promotion', false, 'view'], // form disable
  ['complete', false, 'view'], // form disable
  ['incomplete', false, 'view'], // form disable
  ['submitted-document', false, 'view'], // form disable
  ['finished-document', false, 'view'], // form disable
  ['unclose-tour', false, 'view'], // form disable
  ['close-tour', false, 'view'], // form disable
  ['resolve-approval', false, 'view'], // form disable
  ['order-adjustment', false, 'view'], // form disable
  ['reject', false, 'view'], // form disable
  ['approve', false, 'view'], // form disable
  ['cancel', true, 'view'], // form enable
  ['save', true, 'default'], // form enable
  ['edit', false, 'view'], // form disable
  ['action', false, 'view'], // form disable
  ['create-guest', true, 'default-sales'], // form enable
  ['incomplete', false, 'view'], //form disable
  ['complete', false, 'view'], // form disable
  ['print-invoice', false, 'view'], //form disable
];

const orders = uniskaiActionsOrder.map((item) => item[0]);

export default function UniskaiFormHeaderActions(
  props: UniskaiFormHeaderActionProps,
) {
  const { t } = useTranslation();
  const { data, additionalRefresh, query, saveMutation } = props;
  const { editable, setIsEditable } = useFormState();
  const { pathname } = useRouter();
  const { reset } = useFormContext();

  const isSalesPage = React.useMemo(() => {
    switch (pathname as NavigationRoutes) {
      case NavigationRoutes.TourSalesCreate:
      case NavigationRoutes.TicketSalesCreate:
      case NavigationRoutes.TicketSalesView:
      case NavigationRoutes.HotelSalesCreate:
      case NavigationRoutes.HotelSalesView:
      case NavigationRoutes.CruiseSalesCreate:
      case NavigationRoutes.CruiseSalesView:
      case NavigationRoutes.ActivitySalesCreate:
      case NavigationRoutes.ActivitySalesView:
      case NavigationRoutes.TravelDocumentSalesCreate:
      case NavigationRoutes.TravelDocumentSalesView:
      case NavigationRoutes.ProductSalesCreate:
      case NavigationRoutes.ProductSalesView:
        return true;
      default:
        return false;
    }
  }, [pathname]);

  const actions = React.useMemo<MenuDataType[]>(() => {
    return (props.actions || []).map((action) => {
      if (action.type) {
        const asset = generateUniskaiActionAsset(action.type, t);
        return {
          ...action,
          label: asset.children || '',
          leftSection: asset.leftSection,
          color: asset.color,
          rightSection: asset.rightSection,
        };
      }

      return action;
    });
  }, [props.actions, t]);

  const extendedButtonProps = React.useCallback<
    (type: UniskaiButtonType) => Omit<UniskaiButtonProps, 'type'>
  >(
    (type: UniskaiButtonType) => {
      const isSubmitting =
        typeof saveMutation?.isLoading === 'boolean'
          ? saveMutation?.isLoading === true
          : false;

      switch (type) {
        case 'save':
          return {
            loading: saveMutation?.isLoading,
          };
        case 'cancel':
          return {
            onClick: () => {
              setIsEditable(false);
              reset();
            },
            show: !isSubmitting,
          };
        case 'refresh':
          return {
            onClick: () => {
              query?.refetch();
              additionalRefresh?.();
            },
          };
        case 'edit':
          return {
            onClick: () => {
              setIsEditable(true);
            },
          };

        default:
          return {};
      }
    },
    [additionalRefresh, query, reset, saveMutation?.isLoading, setIsEditable],
  );

  const customs = React.useMemo(() => {
    const customs: UniskaiButtonProps[] = (props.customs || [])
      .map((item) => {
        const index = orders.indexOf(item.type);
        const requirements = uniskaiActionsOrder[index];
        return {
          ...item,
          ...extendedButtonProps(item.type),
          editable: requirements[1],
          show:
            item.show ??
            ((requirements[2] === 'default-sales' && isSalesPage) ||
              (requirements[2] === 'view' && !!data) ||
              requirements[2] === 'default'),
        };
      })
      .filter((item) => {
        const show = item.editable === editable && item.show;
        return show;
      });

    // sort like order
    customs.sort((a, b) => orders.indexOf(a.type) - orders.indexOf(b.type));

    return customs;
  }, [data, editable, extendedButtonProps, isSalesPage, props.customs]);

  return (
    <>
      {customs.map((item) => {
        return <UniskaiButton key={item.type} {...item} isResponsive={false} />;
      })}
      <Menu target={<UniskaiButton type="action" />} data={actions} />
    </>
  );
}
